import { Component, Input, inject, ViewChild, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatRadioModule } from '@angular/material/radio'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatDialog } from '@angular/material/dialog'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'
import { MatTabsModule } from '@angular/material/tabs'
import { ProgressLoaderComponent } from '../common/progress-loader/progress-loader.component'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { Router, RouterLink } from '@angular/router'
import { firstValueFrom } from 'rxjs'
import { TemplateEditorComponent } from '../template-editor.component/template-editor.component'
import { FileUploadRequest } from '../template-editor.component/template-item/template-item.component'
import { MinutesStylingComponent } from '../minutes-styling/minutes-styling.component'
import {
  Meeting,
  MeetingsService,
  AdminAgendaMetadata,
  AdminAgendaAttendee,
  MeetingProcessingStatus,
  MinutesStyling,
  MetricType,
  AgendaType,
  TemplateSourceType,
  AgendaAttendeeType,
  GoverningBodyType,
  MinutesVerboseness,
  MinutesAnonymization,
  MeetingType,
  canUserEdit,
  computeIsCheckingAgendaValidity,
  computeAgendaUploadStatus,
  computeAgendaFailureMessageKey,
  computeHasUsableTemplateSource,
  computeShouldConfirmTemplateReplace,
  computeCurrentStep,
  computeIsTemplateReviewVisible,
  isAgendaExtractionRejectionVisible,
  AgendaUploadStatus,
} from '../meetings.service'
import { TemplatesService } from '../templates.service'
import { Team } from '../teams.service'
import { TemplateItem } from '../template-editor.component/types/template.types'
import { UsersService } from '../users.service'
import { ToastService } from '../toast.service'
import { MetricsService } from '../metrics.service'
import { TemplateSourcePickerComponent } from '../template-source-picker/template-source-picker.component'
import { DocumentUploadInfo, toSourceDocumentFormat } from '../utils'
import { getUploadErrorMessage } from '../uploads.service'
import { MAX_AGENDA_SIZE_IN_BYTES } from '../meeting-document-upload/meeting-document-upload.component'
import { DocumentUploadService } from '../document-upload.service'
import {
  TemplateImportDialog,
  TemplateImportDialogInputData,
  TemplateImportDialogOutputData,
} from '../template-editor.component/template-import-dialog/template-import-dialog'
import { TemplateSaveAsDialog, TemplateSaveAsDialogOutputData } from '../template-editor.component/template-save-as-dialog/template-save-as-dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { MeetingDetailsCardComponent, MeetingDetailsSaveData } from '../meeting-details-card/meeting-details-card.component'
import { MeetingOwnersCardComponent } from './meeting-owners-card/meeting-owners-card.component'
import { StepProgressComponent } from '../common/step-progress/step-progress.component'
import { DurationPipe } from '../duration.pipe'
import { filter, map, Observable, take } from 'rxjs'
import { FilesizePipe } from '../filesize.pipe'
import { FileUploadComponent } from '../file-upload/file-upload.component'
import { injectQueryClient } from '../query'
import { queryKeys } from '../query-keys'
import { resolveSettings } from '../client-settings'
import { BoardPackIngestDialogComponent } from '../support/meetings/support-meeting-details-page/board-pack-ingest-dialog/board-pack-ingest-dialog.component'
import { PdfPreviewDialogComponent } from '../template-editor.component/pdf-preview-dialog/pdf-preview-dialog.component'
import { DownloadsService } from '../downloads.service'
import {
  ManageMeetingEntitiesDialogComponent,
  ManageMeetingEntitiesDialogData,
} from '../meeting/meeting-entities/manage-meeting-entities-dialog/manage-meeting-entities-dialog.component'

@Component({
  selector: 'app-meeting-setup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatTooltipModule,
    TranslatePipe,
    FileUploadComponent,
    TemplateSourcePickerComponent,
    MatIconButtonSizesModule,
    MatTabsModule,
    ProgressLoaderComponent,
    MatProgressSpinnerModule,
    RouterLink,
    TemplateEditorComponent,
    MinutesStylingComponent,
    MeetingDetailsCardComponent,
    MeetingOwnersCardComponent,
    StepProgressComponent,
    DurationPipe,
    FilesizePipe,
  ],
  templateUrl: './meeting-setup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-setup.component.scss',
})
export class MeetingSetupComponent implements OnInit, OnChanges {
  @Input({ required: true })
  meeting!: Meeting

  @Input({ required: true })
  metadata!: AdminAgendaMetadata

  @Input({ required: true })
  team!: Team

  private meetingsService = inject(MeetingsService)
  private templatesService = inject(TemplatesService)
  private usersService = inject(UsersService)
  private toastService = inject(ToastService)
  private metricsService = inject(MetricsService)
  private dialog = inject(MatDialog)
  private translateService = inject(TranslateService)
  private documentUploadService = inject(DocumentUploadService)
  private downloadsService = inject(DownloadsService)
  private router = inject(Router)
  private queryClient = injectQueryClient()

  @ViewChild('recordingFileUpload')
  recordingFileUploadComponent: FileUploadComponent | undefined

  @ViewChild('templateEditor')
  templateEditor: TemplateEditorComponent | undefined

  @ViewChild('templateSourcePicker')
  templateSourcePicker: TemplateSourcePickerComponent | undefined

  @ViewChild(MeetingDetailsCardComponent)
  meetingDetailsCard: MeetingDetailsCardComponent | undefined

  currentUser$ = this.usersService.currentUser$

  templateItems$!: Observable<TemplateItem[]>

  maxAgendaSize = MAX_AGENDA_SIZE_IN_BYTES
  isImportingAgenda = false

  selectedTabIndex = 0
  showAddAttendeeForm = false
  editedAttendeeId: number | null = null
  editedAttendeeFormData: Partial<AdminAgendaAttendee> = {}
  newAttendee: Partial<AdminAgendaAttendee> = {
    firstName: '',
    lastName: '',
    role: null,
    type: AgendaAttendeeType.MEMBER,
    honorifics: null,
  }

  attendeeTypes = Object.values(AgendaAttendeeType)

  isDraggingRecording = false
  recordingUploadProgress = 0
  recordingRemainingTimeSeconds: number | null = null
  isImportingRecording = false
  uploadingFile: { name: string; size: number; isVideo: boolean } | null = null

  patchMeetingMutation = this.meetingsService.patchMutation()
  updateAgendaTypeMutation = this.meetingsService.updateAgendaTypeMutation()
  updateTemplateItemsMutation = this.templatesService.updateTemplateItems()
  loadTemplateMutation = this.templatesService.loadTemplate()
  saveAsTemplateMutation = this.templatesService.saveMeetingTemplateAsModel()
  addAttendeeMutation = this.meetingsService.addAttendeeMutation()
  updateAttendeeMutation = this.meetingsService.updateAttendeeMutation()
  deleteAttendeeMutation = this.meetingsService.deleteAttendeeMutation()
  updateMetadataMutation = this.meetingsService.updateMetadataMutation()
  importRecordingMutation = this.meetingsService.importRecording()
  completeSetupMutation = this.meetingsService.completeSetup()
  removeResourceMutation = this.meetingsService.removeResource()
  removeAllResourcesOfTypeMutation = this.meetingsService.removeAllResourcesOfType()
  extractParticipantsMutation = this.meetingsService.extractParticipantsMutation()

  // Shows the loading state immediately after the ingest request is accepted,
  // bridging the gap until the next poll reflects the server-side job.
  isIngestingBoardPackOptimistic = false
  // Tracks whether a board pack ingestion is being followed, so we can react to
  // its completion (refresh the template) or failure (toast) exactly once.
  private wasIngestingBoardPack = false

  ngOnInit() {
    this.templateItems$ = this.templatesService.getTemplateItems(this.meeting.id).pipe(map((result) => (result.isSuccess ? result.data : [])))
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['meeting']) {
      this.syncBoardPackIngestionState()
    }
  }

  get stepLabels(): string[] {
    if (this.isUploadedMeeting) {
      return [
        'MEETING_SETUP.STEPS.MEETING_DETAILS',
        'MEETING_SETUP.STEPS.TEMPLATE_SOURCE',
        'MEETING_SETUP.STEPS.REVIEW',
        'MEETING_SETUP.STEPS.UPLOAD_RECORDING',
      ]
    }
    if (this.isMultipleAgenda) {
      return ['MEETING_SETUP.STEPS.MEETING_DETAILS', 'MEETING_SETUP.STEPS.AGENDA_TYPE', 'MEETING_SETUP.STEPS.TEMPLATE_SOURCE']
    }
    return ['MEETING_SETUP.STEPS.MEETING_DETAILS', 'MEETING_SETUP.STEPS.AGENDA_TYPE', 'MEETING_SETUP.STEPS.TEMPLATE_SOURCE', 'MEETING_SETUP.STEPS.REVIEW']
  }

  get canEdit() {
    return canUserEdit(this.meeting)
  }

  get isUploadedMeeting(): boolean {
    return this.meeting.type === MeetingType.AUDIO_UPLOAD || this.meeting.type === MeetingType.VIDEO_UPLOAD
  }

  get hasRecordingUploaded(): boolean {
    return this.meeting.uploadedRecording !== null
  }

  get uploadedRecordingFileName(): string | null {
    return this.meeting.uploadedRecording?.fileName ?? null
  }

  get uploadedRecordingIsVideo(): boolean {
    return this.meeting.uploadedRecording?.resourceType === 'USER_UPLOADED_VIDEO'
  }

  get uploadedRecordingSize(): number | null {
    return this.meeting.uploadedRecording?.size ?? null
  }

  get hasUsableTemplateSource(): boolean {
    return computeHasUsableTemplateSource({
      hasAgendaUploaded: this.hasAgendaUploaded,
      templateSourceType: this.meeting.templateSourceType,
      isCheckingAgendaValidity: this.isCheckingAgendaValidity,
      isImportingAgenda: this.isImportingAgenda,
      isAgendaExtractionFailed: this.isAgendaExtractionFailed,
    })
  }

  get currentStep() {
    return computeCurrentStep({
      isUploadedMeeting: this.isUploadedMeeting,
      isMultipleAgenda: this.isMultipleAgenda,
      hasUsableTemplateSource: this.hasUsableTemplateSource,
    })
  }

  get displayedCurrentStep() {
    const hasUnsavedTemplateChanges = this.templateEditor?.hasUnsavedChanges ?? false
    if (this.isUploadedMeeting && (!this.meeting.templateApprovedByUser || hasUnsavedTemplateChanges) && this.currentStep === 4) {
      return 3
    }
    return this.currentStep
  }

  get completedSteps(): number {
    const hasUnsavedTemplateChanges = this.templateEditor?.hasUnsavedChanges ?? false
    if (this.isUploadedMeeting) {
      if (!this.meeting.templateApprovedByUser || hasUnsavedTemplateChanges) {
        return Math.min(this.currentStep - 1, 2)
      }
      if (this.hasRecordingUploaded) {
        return this.stepLabels.length
      }
      return 3
    }
    if (this.isMultipleAgenda) {
      return 3
    }
    if (!this.meeting.templateApprovedByUser || hasUnsavedTemplateChanges) {
      return Math.min(this.currentStep - 1, 3)
    }
    return 4
  }

  get hasTemplateItems(): boolean {
    return (this.templateEditor?.templateItems?.length ?? 0) > 0
  }

  get hasUnsavedChanges(): boolean {
    if (this.isMultipleAgenda) {
      return false
    }
    const reviewStepThreshold = this.isUploadedMeeting ? 3 : 4
    if (this.currentStep < reviewStepThreshold) {
      return false
    }
    if (!this.hasTemplateItems) {
      return false
    }
    return (this.templateEditor?.hasUnsavedChanges ?? false) || !this.meeting.templateApprovedByUser
  }

  get hasUnconfirmedTemplate(): boolean {
    if (this.isMultipleAgenda) {
      return false
    }
    const reviewStepThreshold = this.isUploadedMeeting ? 3 : 4
    if (this.currentStep < reviewStepThreshold) {
      return false
    }
    if (!this.hasTemplateItems) {
      return false
    }
    return !this.meeting.templateApprovedByUser
  }

  get hasAgendaUploaded(): boolean {
    return this.meeting.userUploadedResources.some((resource) => resource.resourceType === 'AGENDA')
  }

  get defaultLevelOfDetail(): MinutesVerboseness {
    return this.meeting.minutesVerboseness ?? resolveSettings(this.team).minutesVerboseness ?? MinutesVerboseness.DETAILED
  }

  get defaultAnonymization(): MinutesAnonymization {
    return this.meeting.minutesAnonymization ?? resolveSettings(this.team).minutesAnonymization ?? MinutesAnonymization.NONE
  }

  get uploadedAgendaFileName(): string | null {
    const agendaResource = this.meeting.userUploadedResources.find((resource) => resource.resourceType === 'AGENDA')
    return agendaResource?.fileName ?? null
  }

  get isExtractingAgenda(): boolean {
    return this.meeting.processingStatus === MeetingProcessingStatus.GENERATING_AGENDA_ITEMS
  }

  get agendaExtractionProgress(): number | null {
    if (!this.isExtractingAgenda) {
      return null
    }
    return this.meeting.agendaExtractionProgress
  }

  get isCheckingAgendaValidity(): boolean {
    return computeIsCheckingAgendaValidity(this.meeting)
  }

  get isPopulatingTemplate(): boolean {
    return this.isExtractingAgenda && !this.isCheckingAgendaValidity
  }

  get isAgendaExtractionFailed(): boolean {
    return isAgendaExtractionRejectionVisible(this.meeting)
  }

  get agendaUploadStatus(): AgendaUploadStatus {
    return computeAgendaUploadStatus({
      isImporting: this.isImportingAgenda,
      isFailed: this.isAgendaExtractionFailed,
      isChecking: this.isCheckingAgendaValidity,
      isUploaded: this.hasAgendaUploaded && this.meeting.templateSourceType === TemplateSourceType.UPLOAD,
    })
  }

  get isInvalidAgendaDocument(): boolean {
    return this.meeting.invalidAgendaReason !== null
  }

  get agendaFailureMessageKey(): string | null {
    return computeAgendaFailureMessageKey(this.meeting.invalidAgendaReason, this.meeting.agendaExtractionFailed)
  }

  get canReplaceAgenda(): boolean {
    return (this.canEdit || this.isAgendaExtractionFailed) && !this.isExtractingAgenda
  }

  get isIngestingBoardPack(): boolean {
    return this.isIngestingBoardPackOptimistic || this.meeting.isIngestingBoardPack
  }

  get boardPackIngestionProgress(): number | null {
    // The server already reports null unless a job is in progress.
    return this.meeting.boardPackIngestionProgress
  }

  get isProcessingTemplate(): boolean {
    return this.isPopulatingTemplate || this.isIngestingBoardPack
  }

  get processingProgress(): number | null {
    return this.isIngestingBoardPack ? this.boardPackIngestionProgress : this.agendaExtractionProgress
  }

  get processingLabelKey(): string {
    if (!this.isIngestingBoardPack) {
      return 'MEETING_SETUP.TEMPLATE_REVIEW.EXTRACTING'
    }
    return (this.boardPackIngestionProgress ?? 0) >= 50
      ? 'MEETING_SETUP.TEMPLATE_REVIEW.BOARD_PACK_ASSIGNING'
      : 'MEETING_SETUP.TEMPLATE_REVIEW.BOARD_PACK_EXTRACTING'
  }

  /** Set up before the meeting, so that the Teams side panel has the entities to mark while it runs. */
  get canSetUpEntities(): boolean {
    return this.meeting.allowMeetingEntities && this.canEdit
  }

  openManageEntities() {
    this.dialog.open<ManageMeetingEntitiesDialogComponent, ManageMeetingEntitiesDialogData>(ManageMeetingEntitiesDialogComponent, {
      data: { meetingId: this.meeting.id },
      width: '760px',
      maxWidth: '95vw',
      maxHeight: '90vh',
    })
  }

  get canIngestBoardPack(): boolean {
    return !this.meeting.isAgendaExtractionInProgress && !this.isAgendaExtractionFailed && this.canEdit && this.hasTemplateItems
  }

  get boardPackResource() {
    return this.meeting.userUploadedResources.find((resource) => resource.resourceType === 'BOARD_PACK') ?? null
  }

  previewBoardPack() {
    const resource = this.boardPackResource
    if (resource === null || resource.downloadUrl === null) {
      return
    }
    this.dialog.open(PdfPreviewDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      height: '90vh',
      data: { downloadUrl: resource.downloadUrl, fileName: resource.fileName },
    })
  }

  downloadBoardPack() {
    const resource = this.boardPackResource
    if (resource === null || resource.downloadUrl === null) {
      return
    }
    this.downloadsService.downloadFromUrl(resource.downloadUrl, resource.fileName)
  }

  async removeBoardPack() {
    const resource = this.boardPackResource
    if (resource === null) {
      return
    }
    try {
      await this.removeResourceMutation.mutateAsync({
        meetingId: this.meeting.id,
        resourceType: resource.resourceType,
        index: resource.index,
        extension: resource.extension,
      })
    } catch (error) {
      console.error('Failed to remove board pack:', error)
    }
  }

  ingestBoardPack() {
    const dialogRef = this.dialog.open(BoardPackIngestDialogComponent, {
      width: '500px',
      data: { meetingId: this.meeting.id, hasExistingBoardPack: this.boardPackResource !== null },
    })

    dialogRef.afterClosed().subscribe((started: boolean | undefined) => {
      if (started === true) {
        this.isIngestingBoardPackOptimistic = true
        this.wasIngestingBoardPack = true
      }
    })
  }

  private syncBoardPackIngestionState() {
    if (this.meeting.isIngestingBoardPack) {
      // The server confirms a job is running — track it and drop the optimistic bridge.
      this.wasIngestingBoardPack = true
      this.isIngestingBoardPackOptimistic = false
      return
    }
    // The server reports no running job. If we were following one (locally started
    // or observed mid-flight), it has now resolved.
    if (this.wasIngestingBoardPack) {
      this.wasIngestingBoardPack = false
      this.isIngestingBoardPackOptimistic = false
      if (this.meeting.boardPackIngestionFailed) {
        this.toastService.show('MEETINGS.ADMIN.BOARD_PACK.INGEST_POST_START_FAILED')
      }
      // The ingestion rewrites template items — refresh the editor's data.
      this.queryClient.invalidateQueries({ queryKey: queryKeys.templates.byMeetingId(this.meeting.id) })
    }
  }

  get isMultipleAgenda(): boolean {
    return this.meeting.agendaType === 'MULTIPLE'
  }

  get isAgendaTypeLocked(): boolean {
    return !this.meeting.isUpcoming && !this.meeting.isOngoing
  }

  get meetingDetailsStepNumber(): number {
    return 1
  }

  get agendaTypeStepNumber(): number {
    return 2
  }

  get templateSourceStepNumber(): number {
    return this.isUploadedMeeting ? 2 : 3
  }

  get reviewStepNumber(): number {
    return this.isUploadedMeeting ? 3 : 4
  }

  get isTemplateReviewVisible(): boolean {
    return computeIsTemplateReviewVisible({
      isMultipleAgenda: this.isMultipleAgenda,
      currentStep: this.currentStep,
      reviewStepNumber: this.reviewStepNumber,
      isPopulatingTemplate: this.isPopulatingTemplate,
      isIngestingBoardPack: this.isIngestingBoardPack,
      isImportingAgenda: this.isImportingAgenda,
      isAgendaExtractionInProgress: this.meeting.isAgendaExtractionInProgress,
      hasMeeting: this.meeting !== null,
      hasMetadata: this.metadata !== null,
    })
  }

  get uploadRecordingStepNumber(): number {
    return 4
  }

  get isCreateTemplateSelected(): boolean {
    return this.meeting.templateSourceType === TemplateSourceType.CREATE
  }

  get isLoadTemplateSelected(): boolean {
    return this.meeting.templateSourceType === TemplateSourceType.LOAD
  }

  get shouldConfirmTemplateReplace(): boolean {
    return computeShouldConfirmTemplateReplace({
      templateSourceType: this.meeting.templateSourceType,
      isCheckingAgendaValidity: this.isCheckingAgendaValidity,
      isImportingAgenda: this.isImportingAgenda,
      isAgendaExtractionFailed: this.isAgendaExtractionFailed,
      isCreateTemplateSelected: this.isCreateTemplateSelected,
      templateItemsCount: this.templateEditor?.templateItems.length ?? 0,
    })
  }

  get attendeeNames(): string[] {
    return this.metadata.attendees.map((attendee) => `${attendee.firstName} ${attendee.lastName}`)
  }

  get anyAttendeeMissingHonorific(): boolean {
    return this.metadata.attendees.some((attendee) => attendee.honorifics === null || attendee.honorifics === undefined || attendee.honorifics.trim() === '')
  }

  formatHonorific(honorific: string | null | undefined): string {
    if (honorific === null || honorific === undefined) {
      return ''
    }
    const trimmed = honorific.trim()
    if (trimmed === '') {
      return ''
    }
    const endsWithPunctuation = /[.!?]$/.test(trimmed)
    return endsWithPunctuation ? trimmed : `${trimmed}.`
  }

  get isGoverningBodyBoard(): boolean {
    // Temporarily disabled — board member anonymisation feature hidden until ready for release
    return false
  }

  get groupedAttendees(): { type: AgendaAttendeeType; attendees: AdminAgendaAttendee[] }[] {
    const groups = new Map<AgendaAttendeeType, AdminAgendaAttendee[]>()

    const typeOrder = [
      AgendaAttendeeType.CHAIR,
      AgendaAttendeeType.MEMBER,
      AgendaAttendeeType.STANDING_ATTENDEE,
      AgendaAttendeeType.ATTENDEE_BY_INVITATION,
      AgendaAttendeeType.OBSERVER,
      AgendaAttendeeType.COMPANY_SECRETARY,
      AgendaAttendeeType.APOLOGY,
      AgendaAttendeeType.OTHER,
    ]

    this.metadata.attendees.forEach((attendee) => {
      const existing = groups.get(attendee.type) ?? []
      groups.set(attendee.type, [...existing, attendee])
    })

    return typeOrder
      .filter((type) => groups.has(type))
      .map((type) => ({
        type,
        attendees: groups.get(type)!,
      }))
  }

  async onUploadConfirmationRequested() {
    const confirmed = await this.showTemplateReplaceConfirmation()
    if (confirmed && this.templateSourcePicker !== undefined) {
      this.templateSourcePicker.openFileDialog()
    }
  }

  onAgendaUploadStarted() {
    this.isImportingAgenda = true
  }

  async onAgendaFileChange(documentUploadInfo: DocumentUploadInfo | null) {
    if (documentUploadInfo === null) {
      this.isImportingAgenda = false
      return
    }
    this.isImportingAgenda = true
    try {
      await this.meetingsService.importAgenda().mutateAsync({
        meetingId: this.meeting.id,
        data: {
          targetResourceExtension: documentUploadInfo.extension,
          uploadKey: documentUploadInfo.key,
          fileName: documentUploadInfo.fileName,
          sourceDocumentFormat: toSourceDocumentFormat(documentUploadInfo.extension),
        },
      })
      this.isImportingAgenda = false
      await this.patchMeetingMutation.mutateAsync({ meetingId: this.meeting.id, data: { templateApprovedByUser: false } })
      this.toastService.show('MEETING_SETUP.TEMPLATE_SOURCE.AGENDA_UPLOADED')
      this.scrollToStep(this.reviewStepNumber)
    } catch (error) {
      console.error('Failed to upload agenda:', error)
      this.isImportingAgenda = false
      const uploadErrorMessage = getUploadErrorMessage(error)
      if (uploadErrorMessage !== null) {
        this.toastService.show(uploadErrorMessage)
      } else {
        this.toastService.show('MEETING_SETUP.TEMPLATE_SOURCE.AGENDA_UPLOAD_FAILED')
      }
    }
  }

  onRecordingUploadCardClick() {
    if (this.recordingFileUploadComponent !== undefined) {
      this.recordingFileUploadComponent.openFileDialog()
    }
  }

  onRecordingDragOver(event: DragEvent) {
    event.preventDefault()
    this.isDraggingRecording = true
  }

  onRecordingDragLeave() {
    this.isDraggingRecording = false
  }

  onRecordingDrop(event: DragEvent) {
    event.preventDefault()
    this.isDraggingRecording = false
    const files = event.dataTransfer?.files ?? null
    if (files !== null && files.length > 0 && this.recordingFileUploadComponent !== undefined) {
      this.recordingFileUploadComponent.fileInputChanged({ target: { files } })
    }
  }

  cancelRecordingUpload() {
    if (this.recordingFileUploadComponent !== undefined) {
      this.recordingFileUploadComponent.removeFile()
    }
    this.recordingUploadProgress = 0
    this.recordingRemainingTimeSeconds = null
    this.uploadingFile = null
  }

  onRecordingProgressChange(event: { progress: number; remainingTimeSeconds: number | null }) {
    this.recordingUploadProgress = event.progress
    this.recordingRemainingTimeSeconds = event.remainingTimeSeconds
    const chosenFile = this.recordingFileUploadComponent?.chosenFile
    if (this.uploadingFile === null && chosenFile !== null && chosenFile !== undefined) {
      this.uploadingFile = {
        name: chosenFile.name,
        size: chosenFile.size,
        isVideo: chosenFile.type.startsWith('video'),
      }
    }
  }

  async onRecordingFileChange(documentUploadInfo: DocumentUploadInfo | null) {
    if (documentUploadInfo === null) {
      return
    }
    this.recordingUploadProgress = 0
    this.recordingRemainingTimeSeconds = null
    this.isImportingRecording = true
    try {
      await this.importRecordingMutation.mutateAsync({
        meetingId: this.meeting.id,
        uploadKey: documentUploadInfo.key,
        fileExtension: documentUploadInfo.extension,
        fileName: documentUploadInfo.fileName,
      })
      this.toastService.show('MEETING_SETUP.UPLOAD_RECORDING.UPLOAD_SUCCESS')
      setTimeout(() => {
        const setupContent = document.querySelector('.setup-content')
        if (setupContent !== null) {
          setupContent.scrollTo({ top: setupContent.scrollHeight, behavior: 'smooth' })
        }
      }, 100)
    } catch (error) {
      console.error('Failed to import recording:', error)
    } finally {
      this.isImportingRecording = false
      this.uploadingFile = null
    }
  }

  async completeSetup() {
    try {
      await this.completeSetupMutation.mutateAsync({ meetingId: this.meeting.id })
      this.toastService.show('MEETING_SETUP.COMPLETE_SETUP.SUCCESS')
      await this.router.navigate(['/app', 'meetings', `${this.meeting.id}`])
    } catch (error) {
      console.error('Failed to complete setup:', error)
    }
  }

  async removeUploadedRecording() {
    if (this.meeting.uploadedRecording === null) {
      return
    }
    const recording = this.meeting.uploadedRecording
    try {
      await this.removeResourceMutation.mutateAsync({
        meetingId: this.meeting.id,
        resourceType: recording.resourceType,
        index: recording.index,
        extension: recording.extension,
      })
    } catch (error) {
      console.error('Failed to remove recording:', error)
    }
  }

  async openTemplateSelectionDialog() {
    if (this.shouldConfirmTemplateReplace) {
      const confirmed = await this.showTemplateReplaceConfirmation()
      if (!confirmed) {
        return
      }
    }

    this.templatesService
      .getTemplateModels()
      .pipe(
        filter((result) => result.isSuccess),
        take(1)
      )
      .subscribe((result) => {
        const dialogData: TemplateImportDialogInputData = {
          templates: result.data,
        }
        const dialogRef = this.dialog.open(TemplateImportDialog, {
          width: '500px',
          data: dialogData,
        })

        dialogRef.afterClosed().subscribe((dialogResult: TemplateImportDialogOutputData | null | undefined) => {
          if (dialogResult !== null && dialogResult !== undefined) {
            this.loadTemplateFromModel(dialogResult.templateId)
          }
        })
      })
  }

  async onDetailsSaved(saveData: MeetingDetailsSaveData) {
    await this.updateMetadataMutation.mutateAsync({
      meetingId: this.meeting.id,
      data: saveData.metadata,
    })
    const meetingPatchData = {
      ...(saveData.dateTime !== null && {
        date: saveData.dateTime.date,
        endDate: saveData.dateTime.endDate,
      }),
      ...(saveData.link !== this.meeting.link && { link: saveData.link }),
    }
    if (Object.keys(meetingPatchData).length > 0) {
      await this.patchMeetingMutation.mutateAsync({
        meetingId: this.meeting.id,
        data: meetingPatchData,
      })
    }
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
  }

  async updateAgendaType(agendaType: AgendaType) {
    await this.updateAgendaTypeMutation.mutateAsync({ meetingId: this.meeting.id, data: { agendaType } })
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
    await this.metricsService.sendMetric(this.meeting.id, MetricType.CHANGE_AGENDA_TYPE, { agendaType })
  }

  async updateMinutesStyling(styling: Partial<MinutesStyling>) {
    await this.patchMeetingMutation.mutateAsync({
      meetingId: this.meeting.id,
      data: { ...styling, templateApprovedByUser: false },
    })
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
    await this.metricsService.sendMetric(this.meeting.id, MetricType.CHANGE_MINUTING_STYLES, styling)
  }

  async confirmTemplate() {
    if (this.templateEditor !== undefined && this.templateEditor.hasUnsavedChanges) {
      const cleanedItems = this.templateEditor.getCleanedTemplateItems()
      await this.saveTemplateItems(cleanedItems)
    }

    await this.patchMeetingMutation.mutateAsync({ meetingId: this.meeting.id, data: { templateApprovedByUser: true } })
    this.toastService.show('MEETING_SETUP.TEMPLATE_REVIEW.TEMPLATE_CONFIRMED')
    if (this.isUploadedMeeting) {
      this.scrollToStep(5)
    }
  }

  private async loadTemplateFromModel(templateId: number) {
    if (this.templateEditor !== undefined) {
      this.templateEditor.clearUnsavedChanges()
    }
    await this.removeUploadedAgenda()
    await this.loadTemplateMutation.mutateAsync({
      meetingId: this.meeting.id,
      templateId,
    })
    this.scrollToStep(this.reviewStepNumber)
  }

  async createBlankTemplate() {
    if (this.shouldConfirmTemplateReplace) {
      const confirmed = await this.showTemplateReplaceConfirmation()
      if (!confirmed) {
        return
      }
    }

    await this.removeUploadedAgenda()
    await this.updateTemplateItemsMutation.mutateAsync({
      meetingId: this.meeting.id,
      items: [],
    })
    await this.patchMeetingMutation.mutateAsync({ meetingId: this.meeting.id, data: { templateApprovedByUser: false } })
    this.toastService.show('MEETING_SETUP.TEMPLATE_SOURCE.BLANK_TEMPLATE_CREATED')
    this.scrollToStep(this.reviewStepNumber)
  }

  private async showTemplateReplaceConfirmation(): Promise<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETING_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.TITLE'),
        description: this.translateService.instant('MEETING_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.DESCRIPTION'),
        confirmLabel: this.translateService.instant('MEETING_SETUP.TEMPLATE_SOURCE.REPLACE_DIALOG.CONFIRM'),
        cancelLabel: this.translateService.instant('COMMON.CANCEL'),
      },
    })
    return await firstValueFrom(dialogRef.afterClosed())
  }

  private async removeUploadedAgenda() {
    const hasAgenda = this.meeting.userUploadedResources.some((resource) => resource.resourceType === 'AGENDA')
    if (!hasAgenda) {
      return
    }
    try {
      await this.removeAllResourcesOfTypeMutation.mutateAsync({
        meetingId: this.meeting.id,
        resourceType: 'AGENDA',
      })
    } catch (error) {
      console.error('Failed to remove agenda:', error)
    }
  }

  onStepClick(stepIndex: number) {
    if (stepIndex + 1 <= this.currentStep) {
      const elementIndex = this.isUploadedMeeting && stepIndex > 0 ? stepIndex + 1 : stepIndex
      const element = document.getElementById(`step-${elementIndex}`)
      if (element !== null) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  private scrollToStep(step: number) {
    const elementId = `step-${step - 1}`
    const element = document.getElementById(elementId)
    if (element !== null) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    const observer = new MutationObserver(() => {
      const target = document.getElementById(elementId)
      if (target !== null) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        observer.disconnect()
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    setTimeout(() => observer.disconnect(), 5000)
  }

  async saveTemplateItems(items: TemplateItem[]) {
    if (this.templateEditor === undefined) {
      return
    }
    try {
      const response = await this.updateTemplateItemsMutation.mutateAsync({
        meetingId: this.meeting.id,
        items,
      })
      this.templateEditor.confirmSaveSuccess()
      this.templateEditor.applyIdMapping(response.clientIdToServerIdMapping)
    } catch (error) {
      this.templateEditor.confirmSaveFailure()
    }
  }

  async deleteAttendee(attendee: AdminAgendaAttendee) {
    await this.deleteAttendeeMutation.mutateAsync({ meetingId: this.meeting.id, attendeeId: attendee.id })
    await this.patchMeetingMutation.mutateAsync({ meetingId: this.meeting.id, data: { templateApprovedByUser: false } })
    this.toastService.show('MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEE_DELETED')
  }

  async updateAttendee(attendee: AdminAgendaAttendee) {
    await this.updateAttendeeMutation.mutateAsync({ meetingId: this.meeting.id, data: attendee })
    await this.patchMeetingMutation.mutateAsync({ meetingId: this.meeting.id, data: { templateApprovedByUser: false } })
    this.toastService.show('MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEE_UPDATED')
  }

  async addAttendee(attendee: Omit<AdminAgendaAttendee, 'id'>) {
    await this.addAttendeeMutation.mutateAsync({ meetingId: this.meeting.id, data: attendee })
    await this.patchMeetingMutation.mutateAsync({ meetingId: this.meeting.id, data: { templateApprovedByUser: false } })
    this.toastService.show('MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEE_ADDED')
  }

  isUploadingParticipantDocument = false

  get isParticipantExtractionInProgress(): boolean {
    return this.isUploadingParticipantDocument || this.meeting.isParticipantExtractionInProgress
  }

  onParticipantExtractionUploadStarted() {
    this.isUploadingParticipantDocument = true
  }

  async onParticipantExtractionFileChange(documentUploadInfo: DocumentUploadInfo | null) {
    if (documentUploadInfo === null) {
      this.isUploadingParticipantDocument = false
      return
    }
    try {
      await this.extractParticipantsMutation.mutateAsync({
        meetingId: this.meeting.id,
        uploadKey: documentUploadInfo.key,
        fileName: documentUploadInfo.fileName,
        fileExtension: documentUploadInfo.extension.toUpperCase(),
      })
      await this.patchMeetingMutation.mutateAsync({ meetingId: this.meeting.id, data: { templateApprovedByUser: false } })
    } catch (error) {
      console.error('Failed to extract participants:', error)
    } finally {
      this.isUploadingParticipantDocument = false
    }
  }

  onTemplateSaved(items: TemplateItem[]) {
    this.saveTemplateItems(items)
  }

  onSaveAsTemplate(items: TemplateItem[]) {
    const dialogRef = this.dialog.open(TemplateSaveAsDialog, {
      width: '400px',
    })

    dialogRef.afterClosed().subscribe(async (result: TemplateSaveAsDialogOutputData | null) => {
      if (result !== null) {
        await this.saveAsTemplateMutation.mutateAsync({
          clientId: this.team.clientId,
          name: result.name,
          items,
        })
      }
    })
  }

  addNewAttendee() {
    this.addAttendee(this.newAttendee as Omit<AdminAgendaAttendee, 'id'>)
    this.newAttendee = { firstName: '', lastName: '', role: null, type: AgendaAttendeeType.MEMBER, honorifics: null }
    this.showAddAttendeeForm = false
  }

  cancelAddAttendee() {
    this.newAttendee = { firstName: '', lastName: '', role: null, type: AgendaAttendeeType.MEMBER, honorifics: null }
    this.showAddAttendeeForm = false
  }

  startEditAttendee(attendee: AdminAgendaAttendee) {
    this.editedAttendeeId = attendee.id
    this.editedAttendeeFormData = { ...attendee }
  }

  cancelEditAttendee() {
    this.editedAttendeeId = null
    this.editedAttendeeFormData = {}
  }

  saveAttendee() {
    this.updateAttendee(this.editedAttendeeFormData as AdminAgendaAttendee)
    this.editedAttendeeId = null
    this.editedAttendeeFormData = {}
  }

  async handleFileUpload(request: FileUploadRequest) {
    if (this.templateEditor === undefined) {
      return
    }
    await this.documentUploadService.uploadCustomDocumentSourceToBlock(this.meeting.id, request.blockId, request.file, this.templateEditor)
  }
}
